import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Stack,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { IoClose, IoWarningOutline, IoLockClosedOutline } from 'react-icons/io5';
import { Icon } from '@iconify/react';

interface DeleteClassModalProps {
  open: boolean;
  onClose: () => void;
  classItem: {
    id: string;
    name?: string;
    title?: string;
    enrolledCount?: number;
    cohortCount?: number;
    cohorts?: { id: string; name: string }[];
  } | null;
  onConfirm: (id: string) => Promise<void>;
  onViewCohorts?: (classId: string) => void;
  isDeleting?: boolean;
}

export const DeleteClassModal: React.FC<DeleteClassModalProps> = ({
  open,
  onClose,
  classItem,
  onConfirm,
  onViewCohorts,
  isDeleting = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const className = classItem?.name || classItem?.title || 'this class';
  const cohortCount = classItem?.cohortCount ?? (classItem?.cohorts?.length ?? 0);
  const hasAssignedCohorts = cohortCount > 0;

  useEffect(() => {
    if (open) {
      setTypedConfirmation('');
      setIsAcknowledged(false);
      setError(null);
    }
  }, [open, classItem]);

  const handleDelete = async () => {
    if (!classItem || hasAssignedCohorts) return;
    try {
      setError(null);
      await onConfirm(classItem.id);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete Grade Level.');
    }
  };

  const isConfirmationMatched = typedConfirmation.trim().toLowerCase() === className.trim().toLowerCase();
  const isDeleteEnabled = isConfirmationMatched && isAcknowledged && !isDeleting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5, p: 0.5 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.75,
              bgcolor: hasAssignedCohorts ? 'rgba(245, 158, 11, 0.12)' : 'rgba(239, 68, 68, 0.12)',
              color: hasAssignedCohorts ? '#D97706' : '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {hasAssignedCohorts ? <IoLockClosedOutline size={20} /> : <IoWarningOutline size={20} />}
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" fontSize="1.125rem">
            {hasAssignedCohorts ? 'Cannot Delete Grade Level' : 'Delete Grade Level (Permanent Action)'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 1.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {hasAssignedCohorts ? (
          /* BLOCKED STATE: Cohorts are assigned */
          <Stack spacing={2}>
            <Alert
              severity="warning"
              icon={<Icon icon="solar:shield-warning-bold-duotone" width={22} />}
              sx={{
                borderRadius: 2,
                bgcolor: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="#B45309" sx={{ mb: 0.5 }}>
                Active Cohort Sections Assigned
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
                <strong>{className}</strong> currently contains <strong>{cohortCount}</strong> active cohort section(s). To protect scholar data and classroom integrity, a grade level cannot be deleted while cohort sections belong to it.
              </Typography>
            </Alert>

            {classItem?.cohorts && classItem.cohorts.length > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                  Assigned Cohort Sections:
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {classItem.cohorts.map((cohort) => (
                    <Chip
                      key={cohort.id}
                      label={cohort.name}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ color: '#D97706', display: 'flex', alignItems: 'center', flexShrink: 0, mt: '2px' }}>
                <Icon icon="solar:info-circle-bold-duotone" width={18} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                <strong>Required step:</strong> Please delete or reassign the cohort sections above to another grade level first. Once no cohorts are attached, this grade level can be deleted safely.
              </Typography>
            </Stack>
          </Stack>
        ) : (
          /* ALLOWED STATE: Double Confirmation Required */
          <Stack spacing={2}>
            {/* Primary Warning Alert */}
            <Alert
              severity="error"
              icon={<Icon icon="solar:danger-triangle-bold-duotone" width={22} />}
              sx={{
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="#DC2626" sx={{ mb: 0.5 }}>
                High-Risk Permanent Deletion
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5 }}>
                You are about to permanently delete the grade container <strong>"{className}"</strong>. This will delete the grade record and unenroll legacy associated students.
              </Typography>
            </Alert>

            {/* Double Confirmation Input */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.75 }}>
                Security Check: Type the exact grade name <strong style={{ color: '#0F172A' }}>"{className}"</strong> below to confirm:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={typedConfirmation}
                onChange={(e) => setTypedConfirmation(e.target.value)}
                placeholder={`Type "${className}" here`}
                autoFocus
                InputProps={{
                  sx: { borderRadius: 1.5, fontSize: '0.875rem', bgcolor: 'background.paper' },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isAcknowledged}
                    onChange={(e) => setIsAcknowledged(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.8125rem' }}>
                    I understand this action cannot be undone.
                  </Typography>
                }
                sx={{ mt: 1.25 }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600, textTransform: 'none' }}>
          Cancel
        </Button>

        {hasAssignedCohorts ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onClose();
              if (onViewCohorts && classItem) {
                onViewCohorts(classItem.id);
              }
            }}
            startIcon={<Icon icon="solar:users-group-two-rounded-bold-duotone" width={16} />}
            sx={{
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              px: 2,
              boxShadow: 'none',
            }}
          >
            Go to Assigned Cohorts
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!isDeleteEnabled}
            startIcon={<Icon icon="solar:trash-bin-trash-bold-duotone" width={16} />}
            sx={{
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              px: 2.5,
              boxShadow: 'none',
            }}
          >
            {isDeleting ? 'Deleting...' : 'Permanently Delete Grade Level'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
